import { useState } from "react";
import { motion } from "framer-motion";
import { Beer, Wine, Package, MapPin, Clock } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import { FIND_US } from "../../data/content";

const TABS = [
  { id: "bottles", label: "Bottles", Icon: Wine },
  { id: "cans", label: "Cans", Icon: Package },
  { id: "tap", label: "On the Tap", Icon: Beer },
];

const Soon = ({ label }) => (
  <div className="flex flex-col items-center rounded-[1.5rem] border border-forest/25 bg-white/60 px-6 py-16 text-center" data-testid={`find-us-${label.toLowerCase()}-soon`}>
    <Clock className="h-8 w-8 text-forest" />
    <p className="mt-5 font-display text-4xl font-semibold text-forest-deep sm:text-5xl">{label}: <span className="italic text-forest">coming soon</span></p>
    <p className="mt-3 max-w-md text-base text-forest-deep">We're getting ready. Check back shortly to find out where to pick up Himachal <span className="font-deva">हो!</span> {label.toLowerCase()}.</p>
  </div>
);

const Taps = () => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="find-us-tap-list">
    {FIND_US.map((c, i) => (
      <Reveal key={c.city} delay={i * 0.08} className="rounded-[1.5rem] border border-forest/20 bg-white p-7" data-testid={`find-us-city-${c.city.toLowerCase()}`}>
        <div className="flex items-baseline justify-between border-b border-forest/15 pb-4">
          <h3 className="font-display text-3xl font-semibold text-forest">{c.city}</h3>
        </div>
        <ul className="mt-4 space-y-3">
          {c.venues.map(([name, area]) => (
            <li key={name + area} className="flex items-start gap-3">
              <MapPin className="mt-1 h-4 w-4 shrink-0 text-forest" />
              <span>
                <span className="block font-medium text-forest-deep">{name}</span>
                {area && <span className="block text-sm text-forest-deep/85">{area}</span>}
              </span>
            </li>
          ))}
          <li className="pl-7 font-display text-lg italic text-forest" data-testid={`find-us-more-${c.city.toLowerCase()}`}>and more…</li>
        </ul>
      </Reveal>
    ))}
  </div>
);

export default function FindUs() {
  const [tab, setTab] = useState("tap");
  return (
    <section id="find-us" className="relative bg-mist py-24 text-forest-deep lg:py-32" data-testid="find-us-section">
      <div className="wrap">
        <p className="eyebrow text-forest">Where to find us</p>
        <MaskLines className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" lines={["Raise a glass", <span key="b" className="italic text-forest">near you.</span>]} />

        <div className="mt-10 grid w-full grid-cols-3 gap-1 rounded-full border border-forest-deep/20 bg-white/60 p-1.5 sm:inline-flex sm:w-auto" role="tablist" data-testid="find-us-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} role="tab" aria-selected={tab === id} data-testid={`find-us-tab-${id}`} onClick={() => setTab(id)} className="relative flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-2 py-3 text-[12px] font-medium uppercase tracking-[0.1em] sm:px-6 sm:text-sm sm:tracking-[0.14em]">
              {tab === id && <motion.span layoutId="find-pill" className="absolute inset-0 rounded-full bg-forest" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
              <Icon className={`relative hidden h-4 w-4 sm:block ${tab === id ? "text-white" : "text-forest"}`} />
              <span className={`relative ${tab === id ? "text-white" : "text-forest-deep"}`}>{label}</span>
            </button>
          ))}
        </div>

        <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10">
          {tab === "tap" ? <Taps /> : <Soon label={tab === "cans" ? "Cans" : "Bottles"} />}
        </motion.div>
      </div>
    </section>
  );
}
